import React, { useState, useEffect, useCallback } from 'react';
import './ColorPaletteGenerator.css';

/**
 * Simple Color Palette Generator - nur für SpaceCreationWizard
 * Generiert Farben, speichert sie lokal, übergibt sie beim Space-Erstellen
 */
const SimpleColorPaletteGenerator = ({ currentColors, onColorsChange, onPalettesChange }) => {
    const [selectedStyle, setSelectedStyle] = useState('vibrant');
    const [currentPalette, setCurrentPalette] = useState(currentColors);
    const [lockedColors, setLockedColors] = useState([false, false, false, false, false]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [savedPalettes, setSavedPalettes] = useState([]);
    const [paletteHistory, setPaletteHistory] = useState([]);
    const [historyPosition, setHistoryPosition] = useState(0);

    // Farbstil-Definitionen (gleiche wie im originalen Generator)
    const colorStyles = {
        vibrant: {
            name: 'Vibrant',
            description: 'Bold and energetic colors',
            saturation: [70, 100],
            lightness: [40, 70]
        },
        soft: {
            name: 'Soft',
            description: 'Gentle and calming colors',
            saturation: [30, 60],
            lightness: [60, 85]
        },
        pastel: {
            name: 'Pastel',
            description: 'Delicate and light colors',
            saturation: [15, 35],
            lightness: [80, 95]
        },
        monochrome: {
            name: 'Monochrome',
            description: 'Neutral grays and blacks',
            saturation: [0, 20],
            lightness: [20, 80]
        },
        nature: {
            name: 'Nature',
            description: 'Earthy and organic colors',
            saturation: [25, 60],
            lightness: [30, 70]
        }
    };

    // HSL zu Hex konvertieren
    const hslToHex = (h, s, l) => {
        h = h / 360;
        s = s / 100;
        l = l / 100;
        
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
        };
        
        let r, g, b;
        if (s === 0) {
            r = g = b = l;
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1/3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1/3);
        }
        
        const toHex = (c) => {
            const hex = Math.round(c * 255).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };
        
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    // Palette generieren mit mehr Variation
    const generateHarmoniousPalette = useCallback((style) => {
        const styleConfig = colorStyles[style];
        const colors = [];
        const primaryHue = Math.random() * 360;
        
        // Verschiedene Harmoniemodelle für mehr Vielfalt
        const harmonyModes = ['analogous', 'triadic', 'splitComplementary', 'tetradic', 'monochromatic'];
        const selectedHarmony = harmonyModes[Math.floor(Math.random() * harmonyModes.length)];
        
        if (style === 'monochrome') {
            const accentHue = primaryHue;
            colors.push(hslToHex(accentHue, 25, 45));
            colors.push(hslToHex(0, 0, 15));
            colors.push(hslToHex(0, 0, 25));
            colors.push(hslToHex(0, 0, 85));
            colors.push(hslToHex(0, 0, 95));
        } else {
            const baseHue = primaryHue % 360;
            
            // Generiere Hue-Variationen basierend auf Harmoniemodell (gleiche Logik wie Hauptgenerator)
            let hueVariations = [];
            switch(selectedHarmony) {
                case 'analogous':
                    hueVariations = [
                        baseHue,
                        (baseHue + 15 + Math.random() * 30) % 360,
                        (baseHue + 30 + Math.random() * 30) % 360,
                        (baseHue - 15 + Math.random() * 30) % 360,
                        (baseHue - 30 + Math.random() * 30) % 360
                    ];
                    break;
                case 'triadic':
                    hueVariations = [
                        baseHue,
                        (baseHue + 120 + Math.random() * 20 - 10) % 360,
                        (baseHue + 240 + Math.random() * 20 - 10) % 360,
                        (baseHue + 60 + Math.random() * 40) % 360,
                        (baseHue + 300 + Math.random() * 40) % 360
                    ];
                    break;
                case 'splitComplementary':
                    hueVariations = [
                        baseHue,
                        (baseHue + 150 + Math.random() * 20) % 360,
                        (baseHue + 210 + Math.random() * 20) % 360,
                        (baseHue + 30 + Math.random() * 30) % 360,
                        (baseHue + 330 + Math.random() * 30) % 360
                    ];
                    break;
                case 'tetradic':
                    hueVariations = [
                        baseHue,
                        (baseHue + 90 + Math.random() * 20) % 360,
                        (baseHue + 180 + Math.random() * 20) % 360,
                        (baseHue + 270 + Math.random() * 20) % 360,
                        (baseHue + 45 + Math.random() * 30) % 360
                    ];
                    break;
                default:
                    hueVariations = [
                        baseHue,
                        baseHue + Math.random() * 10 - 5,
                        baseHue + Math.random() * 10 - 5,
                        baseHue + Math.random() * 15 - 7.5,
                        baseHue + Math.random() * 15 - 7.5
                    ];
            }
            
            // Sättigungswerte mit mehr Variation
            const satRange = styleConfig.saturation[1] - styleConfig.saturation[0];
            const baseSat = styleConfig.saturation[0] + Math.random() * satRange;
            const satVariations = [
                Math.max(10, baseSat + Math.random() * 30 - 15),
                Math.max(5, baseSat + Math.random() * 40 - 20),
                Math.max(5, baseSat + Math.random() * 25 - 12.5),
                Math.max(3, baseSat + Math.random() * 35 - 17.5),
                Math.max(2, baseSat + Math.random() * 20 - 10)
            ];
            
            if (style === 'pastel') {
                const basePastelSat = 10 + Math.random() * 25;
                colors.push(hslToHex(hueVariations[0], Math.min(45, basePastelSat + Math.random() * 20), 70 + Math.random() * 15));
                colors.push(hslToHex(hueVariations[1], basePastelSat * 0.8, 55 + Math.random() * 15));
                colors.push(hslToHex(hueVariations[2], basePastelSat * 0.9, 65 + Math.random() * 15));
                colors.push(hslToHex(hueVariations[3], basePastelSat * 0.6, 85 + Math.random() * 8));
                colors.push(hslToHex(hueVariations[4], basePastelSat * 0.4, 92 + Math.random() * 6));
            } else if (style === 'nature') {
                const earthyHueRanges = [
                    [10, 50], [50, 90], [90, 150], [150, 200], [200, 240]
                ];
                const selectedRanges = earthyHueRanges.sort(() => Math.random() - 0.5).slice(0, 5);
                const earthyHues = selectedRanges.map(([min, max]) => min + Math.random() * (max - min));
                
                colors.push(hslToHex(earthyHues[0], 35 + Math.random() * 35, 40 + Math.random() * 25));
                colors.push(hslToHex(earthyHues[1], 25 + Math.random() * 30, 15 + Math.random() * 20));
                colors.push(hslToHex(earthyHues[2], 30 + Math.random() * 25, 20 + Math.random() * 20));
                colors.push(hslToHex(earthyHues[3], 10 + Math.random() * 20, 70 + Math.random() * 15));
                colors.push(hslToHex(earthyHues[4], 5 + Math.random() * 15, 85 + Math.random() * 12));
            } else {
                // Helligkeitswerte mit mehr Variation
                const lightnessRange = styleConfig.lightness[1] - styleConfig.lightness[0];
                const baseLightness = styleConfig.lightness[0] + Math.random() * lightnessRange;
                const lightnessVariations = [
                    Math.max(20, baseLightness + Math.random() * 25 - 12.5),
                    Math.max(8, baseLightness * 0.3 + Math.random() * 15),
                    Math.max(15, baseLightness * 0.4 + Math.random() * 20),
                    Math.min(95, baseLightness * 1.2 + Math.random() * 15),
                    Math.min(98, baseLightness * 1.3 + Math.random() * 10)
                ];
                
                colors.push(hslToHex(hueVariations[0], satVariations[0], lightnessVariations[0]));
                colors.push(hslToHex(hueVariations[1], satVariations[1], lightnessVariations[1]));
                colors.push(hslToHex(hueVariations[2], satVariations[2], lightnessVariations[2]));
                colors.push(hslToHex(hueVariations[3], satVariations[3], lightnessVariations[3]));
                colors.push(hslToHex(hueVariations[4], satVariations[4], lightnessVariations[4]));
            }
        }
        
        return colors;
    }, []);

    // Neue Palette generieren
    const generateNewPalette = useCallback(() => {
        setIsGenerating(true);
        
        setTimeout(() => {
            const newColors = generateHarmoniousPalette(selectedStyle);
            const updatedPalette = currentPalette.map((color, index) => 
                lockedColors[index] ? color : newColors[index]
            );
            
            // Add to history
            setPaletteHistory(prev => [currentPalette, ...prev.slice(0, 9)]);
            setHistoryPosition(0);
            
            setCurrentPalette(updatedPalette);
            setIsGenerating(false);
        }, 200);
    }, [selectedStyle, currentPalette, lockedColors, generateHarmoniousPalette]);

    // Palette speichern (lokal)
    const savePalette = () => {
        const paletteData = {
            id: `palette_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: `Palette ${savedPalettes.length + 1}`,
            colors: {
                color1: currentPalette[0],
                color2: currentPalette[1],
                color3: currentPalette[2],
                color4: currentPalette[3],
                color5: currentPalette[4]
            },
            style: selectedStyle,
            createdAt: new Date().toISOString()
        };
        
        const updatedPalettes = [...savedPalettes, paletteData];
        setSavedPalettes(updatedPalettes);
        
        // Notify parent
        if (onPalettesChange) {
            onPalettesChange(updatedPalettes);
        }
        
        console.log('🎨 Simple Generator: Palette saved locally:', paletteData.name);
    };

    // Palette laden
    const loadPalette = (palette) => {
        const paletteColors = [
            palette.colors.color1,
            palette.colors.color2,
            palette.colors.color3,
            palette.colors.color4,
            palette.colors.color5
        ];
        setCurrentPalette(paletteColors);
        
        // Apply colors
        const colorObject = {
            color1: palette.colors.color1,
            color2: palette.colors.color2,
            color3: palette.colors.color3,
            color4: palette.colors.color4,
            color5: palette.colors.color5
        };
        onColorsChange(colorObject, true);
    };

    // Palette löschen
    const deletePalette = (paletteId) => {
        const updatedPalettes = savedPalettes.filter(p => p.id !== paletteId);
        setSavedPalettes(updatedPalettes);
        
        // Notify parent
        if (onPalettesChange) {
            onPalettesChange(updatedPalettes);
        }
    };

    // Auto-apply colors
    useEffect(() => {
        if (currentPalette.length === 5) {
            const colorObject = {
                color1: currentPalette[0],
                color2: currentPalette[1],
                color3: currentPalette[2],
                color4: currentPalette[3],
                color5: currentPalette[4]
            };
            onColorsChange(colorObject, false);
        }
    }, [currentPalette]); // Removed onColorsChange from dependencies to prevent infinite loop

    // Keyboard events
    useEffect(() => {
        const handleKeyPress = (event) => {
            const activeElement = document.activeElement;
            const isInputField = activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.contentEditable === 'true'
            );
            
            if (!isInputField && event.code === 'Space') {
                event.preventDefault();
                generateNewPalette();
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [generateNewPalette]);

    return (
        <div className="color-palette-generator-inline">
            {/* Saved Palettes Section */}
            {savedPalettes.length > 0 && (
                <div className="saved-palettes-section">
                    <h4 className="saved-palettes-title">Saved Palettes</h4>
                    <div className="saved-palettes-grid">
                        {savedPalettes.map((palette) => (
                            <div 
                                key={palette.id}
                                className="saved-palette-item"
                                onClick={() => loadPalette(palette)}
                            >
                                <div className="saved-palette-preview">
                                    {Object.values(palette.colors).map((color, index) => (
                                        <div 
                                            key={index}
                                            className="saved-palette-color"
                                            style={{ backgroundColor: color }}
                                        ></div>
                                    ))}
                                </div>
                                <div className="saved-palette-info">
                                    <span className="saved-palette-name">{palette.name}</span>
                                    <button 
                                        className="delete-palette-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deletePalette(palette.id);
                                        }}
                                        title="Delete palette"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Style Selector */}
            <div className="style-selector-inline">
                <div className="style-grid-inline">
                    {Object.entries(colorStyles).map(([key, style]) => (
                        <button
                            key={key}
                            className={`style-option-inline ${selectedStyle === key ? 'active' : ''}`}
                            onClick={() => setSelectedStyle(key)}
                            title={style.description}
                        >
                            <span className="style-name">{style.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Color Palette */}
            <div className="color-palette-inline">
                {currentPalette.map((color, index) => {
                    const colorLabels = ['Accent', 'Dark BG', 'Dark Text', 'Light Content', 'Light BG'];
                    return (
                        <div key={index} className="palette-color-bar">
                            <div 
                                className="palette-color-surface-inline"
                                style={{ backgroundColor: color }}
                            >
                                <div className="palette-color-info-inline">
                                    <span className="palette-color-label">{colorLabels[index]}</span>
                                    <span className="palette-color-hex">{color}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {/* Controls */}
            <div className="palette-controls-inline">
                <div className="spacebar-hint-inline">
                    <i className="fas fa-keyboard"></i>
                    Press Spacebar for new colors
                </div>
                <div className="palette-action-buttons">
                    <button 
                        className="save-palette-btn-inline"
                        onClick={savePalette}
                        title="Save current palette"
                    >
                        <i className="fas fa-bookmark"></i>
                        Save Palette
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SimpleColorPaletteGenerator;
